import { useState, useEffect, useMemo } from "react";
import { useAuth, api } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { compressImage } from "@/utils/imageCompression";
import {
  Plus, MoreVertical, Pencil, Trash2, Car, Users, Search,
  CalendarIcon, Gauge, RefreshCw, ChevronsUpDown, Check, Camera, Loader2, ScanLine, Upload,
} from "lucide-react";
import { getBrandList, getModelList } from "@/data/carBrands";
import { cn } from "@/lib/utils";
import SmartScanDialog from "@/components/SmartScanDialog";
import VoiceInputButton from "@/components/VoiceInputButton";
import BarcodeScanner from "@/components/BarcodeScanner";
import VinPhotoScanner from "@/components/VinPhotoScanner";

const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [inspectionDate, setInspectionDate] = useState(null);
  const [registrationDate, setRegistrationDate] = useState(null);
  const [brandOpen, setBrandOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [manualModelInput, setManualModelInput] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanFile, setScanFile] = useState(null);
  const [smartScanOpen, setSmartScanOpen] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    license_plate: "",
    brand: "",
    model: "",
    model_description: "",
    year: new Date().getFullYear(),
    vin: "",
    mileage: "",
    last_inspection: "",
    first_registration: "",
  });

  const brandList = useMemo(() => getBrandList(), []);
  const modelList = useMemo(() => getModelList(formData.brand), [formData.brand]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [vehiclesRes, customersRes] = await Promise.all([
        api.get("/api/vehicles"),
        api.get("/api/customers"),
      ]);
      setVehicles(vehiclesRes.data);
      setCustomers(customersRes.data);
    } catch (err) {
      setError("Fehler beim Laden der Daten");
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vin || formData.vin.length !== 17) {
      toast.error("Fahrgestellnummer (VIN) muss genau 17 Zeichen lang sein");
      return;
    }
    try {
      const regDateStr = registrationDate ? format(registrationDate, "yyyy-MM-dd") : null;
      const yearFromReg = registrationDate ? registrationDate.getFullYear() : parseInt(formData.year);
      const data = {
        ...formData,
        year: yearFromReg,
        mileage: formData.mileage ? parseInt(formData.mileage) : null,
        last_inspection: inspectionDate ? format(inspectionDate, "yyyy-MM-dd") : null,
        first_registration: regDateStr,
      };

      let vehicleId = editingVehicle?.id;
      if (editingVehicle) {
        const { customer_id, ...updateData } = data;
        await api.put(`/api/vehicles/${editingVehicle.id}`, updateData);
        toast.success("Fahrzeug aktualisiert");
      } else {
        const res = await api.post("/api/vehicles", data);
        vehicleId = res.data.id;
        toast.success("Fahrzeug erstellt");
      }
      
      // Upload Fahrzeugschein image if scanned
      if (scanFile && vehicleId) {
        try {
          // Compress before upload (smartphone photos can be 10-20 MB,
          // which often fails on mobile networks or hits Cloudinary limits)
          const compressed = await compressImage(scanFile, { maxWidth: 1920, quality: 0.85 });
          const docForm = new FormData();
          docForm.append("file", compressed);
          await api.post(`/api/vehicles/${vehicleId}/fahrzeugschein-upload`, docForm, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          toast.success("Fahrzeugschein bei Fahrzeug & Kunde gespeichert");
        } catch (err) {
          console.error("Fahrzeugschein upload failed:", err);
          toast.error(
            err.response?.data?.detail ||
              "Fahrzeugschein-Upload fehlgeschlagen. Fahrzeug wurde gespeichert, bitte das Bild erneut hochladen."
          );
          // Don't reset form so user can retry without re-scanning
          fetchData();
          return;
        }
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      customer_id: vehicle.customer_id,
      license_plate: vehicle.license_plate,
      brand: vehicle.brand,
      model: vehicle.model,
      model_description: vehicle.model_description || "",
      year: vehicle.year,
      vin: vehicle.vin || "",
      mileage: vehicle.mileage || "",
      last_inspection: vehicle.last_inspection || "",
      first_registration: vehicle.first_registration || "",
    });
    setManualModelInput(!getModelList(vehicle.brand).includes(vehicle.model));
    setInspectionDate(vehicle.last_inspection ? new Date(vehicle.last_inspection) : null);
    setRegistrationDate(vehicle.first_registration ? new Date(vehicle.first_registration) : null);
    setDialogOpen(true);
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm("Fahrzeug und alle zugehörigen Aufträge löschen?")) return;
    try {
      await api.delete(`/api/vehicles/${vehicleId}`);
      toast.success("Fahrzeug gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "", license_plate: "", brand: "", model: "", model_description: "",
      year: new Date().getFullYear(), vin: "", mileage: "",
      last_inspection: "", first_registration: "",
    });
    setInspectionDate(null);
    setRegistrationDate(null);
    setEditingVehicle(null);
    setManualModelInput(false);
    setScanning(false);
    setScanPreview(null);
    setScanFile(null);
  };

  const handleScanRegistration = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setScanning(true);
    setScanPreview(URL.createObjectURL(file));

    try {
      // Compress large smartphone photos (10-20MB) before sending to LLM
      const compressed = await compressImage(file, { maxWidth: 1920, quality: 0.85 });
      setScanFile(compressed);

      // Convert to base64
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(compressed);
      });

      // Send to OCR
      const res = await api.post("/api/vehicles/scan-registration", {
        image_base64: base64,
        ocr_type: "registration",
      }, { timeout: 60000 });
      
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        
        // Find matching customer by halter name or create reference
        let matchedCustomerId = "";
        if (d.halter_name) {
          const match = customers.find((c) => {
            const fullName = `${c.last_name || ""} ${c.first_name || ""}`.toLowerCase();
            return fullName.includes(d.halter_name.toLowerCase()) || 
                   (d.halter_vorname && fullName.includes(d.halter_vorname.toLowerCase()));
          });
          if (match) matchedCustomerId = match.id;
        }

        // Find matching brand
        const brandMatch = brandList.find((b) => 
          b.toLowerCase() === (d.marke || "").toLowerCase()
        );

        // Parse Erstzulassung
        let regDate = null;
        let regYear = new Date().getFullYear();
        if (d.erstzulassung) {
          const parts = d.erstzulassung.split(".");
          if (parts.length === 3) {
            regDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
            regYear = parseInt(parts[2]);
            setRegistrationDate(regDate);
          }
        }

        // Build model description from typ/kraftstoff/leistung
        const descParts = [];
        if (d.typ) descParts.push(d.typ);
        if (d.kraftstoff) descParts.push(d.kraftstoff);
        if (d.leistung_kw) descParts.push(`${d.leistung_kw} kW`);
        if (d.hubraum) descParts.push(`${d.hubraum} ccm`);

        setFormData({
          ...formData,
          customer_id: matchedCustomerId || formData.customer_id,
          license_plate: (d.kennzeichen || "").toUpperCase(),
          brand: brandMatch || d.marke || "",
          model: d.modell || "",
          model_description: descParts.join(", "),
          vin: (d.vin || "").toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17),
          year: regYear,
          mileage: formData.mileage,
          first_registration: regDate ? format(regDate, "yyyy-MM-dd") : "",
        });

        // If brand found in list, check if model is in list
        if (brandMatch) {
          const models = getModelList(brandMatch);
          if (!models.includes(d.modell || "")) {
            setManualModelInput(true);
          }
        } else {
          setManualModelInput(true);
        }

        // Store halter data for customer creation hint
        if (d.halter_name && !matchedCustomerId) {
          toast.info(
            `Halter erkannt: ${d.halter_vorname || ""} ${d.halter_name}. Bitte Kunde manuell auswählen oder zuerst anlegen.`,
            { duration: 8000 }
          );
        }

        // Image will be stored after vehicle creation via fahrzeugschein-upload endpoint

        toast.success("Fahrzeugschein erfolgreich gescannt!");
      } else {
        toast.error(res.data.error || "Scannen fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Fehler beim Scannen des Fahrzeugscheins");
      console.error(err);
    } finally {
      setScanning(false);
      e.target.value = "";
    }
  };

  const filteredVehicles = vehicles.filter((v) => {
    const term = searchTerm.toLowerCase();
    return (
      v.license_plate.toLowerCase().includes(term) ||
      v.brand.toLowerCase().includes(term) ||
      v.model.toLowerCase().includes(term) ||
      (v.vin && v.vin.toLowerCase().includes(term)) ||
      (v.customer_name && v.customer_name.toLowerCase().includes(term))
    );
  });

  const sortedCustomers = useMemo(
    () => [...customers].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")),
    [customers]
  );

  const handleSmartScanComplete = (action, ocrData, file) => {
    if (action === "create" && ocrData) {
      // Pre-fill the vehicle form with OCR data
      const brandMatch = brandList.find((b) => b.toLowerCase() === (ocrData.marke || "").toLowerCase());
      let regDate = null;
      let regYear = new Date().getFullYear();
      if (ocrData.erstzulassung) {
        const parts = ocrData.erstzulassung.split(".");
        if (parts.length === 3) {
          regDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
          regYear = parseInt(parts[2]);
          setRegistrationDate(regDate);
        }
      }
      const descParts = [];
      if (ocrData.typ) descParts.push(ocrData.typ);
      if (ocrData.kraftstoff) descParts.push(ocrData.kraftstoff);
      if (ocrData.leistung_kw) descParts.push(`${ocrData.leistung_kw} kW`);

      setFormData({
        customer_id: "",
        license_plate: (ocrData.kennzeichen || "").toUpperCase(),
        brand: brandMatch || ocrData.marke || "",
        model: ocrData.modell || "",
        model_description: descParts.join(", "),
        vin: (ocrData.vin || "").toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17),
        year: regYear,
        mileage: "",
        first_registration: regDate ? format(regDate, "yyyy-MM-dd") : "",
      });
      if (brandMatch && !getModelList(brandMatch).includes(ocrData.modell || "")) {
        setManualModelInput(true);
      }
      setDialogOpen(true);
    } else {
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="mt-2 text-sm text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">{error}</p>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: "Barlow Condensed" }}>
            Fahrzeugverwaltung
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">{vehicles.length} Fahrzeuge gesamt</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            className="gap-2 flex-1 sm:flex-none"
            onClick={() => setSmartScanOpen(true)}
            data-testid="smart-scan-button"
          >
            <ScanLine className="w-4 h-4" />
            Fahrzeugschein scannen
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2 w-full sm:w-auto" data-testid="add-vehicle-button">
              <Plus className="w-4 h-4" /> Neues Fahrzeug
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="vehicle-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Barlow Condensed" }} className="text-xl">
                {editingVehicle ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}
              </DialogTitle>
            </DialogHeader>

            {/* Fahrzeugschein Scan / Upload */}
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center" data-testid="scan-registration-area">
              {scanPreview ? (
                <div className="space-y-2">
                  <img src={scanPreview} alt="Fahrzeugschein" className="max-h-32 mx-auto rounded object-contain" />
                  {scanning && (
                    <div className="flex items-center justify-center gap-2 text-sm text-primary">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Fahrzeugschein wird analysiert...
                    </div>
                  )}
                </div>
              ) : editingVehicle?.fahrzeugschein_url ? (
                <div className="space-y-2">
                  <img src={editingVehicle.fahrzeugschein_url} alt="Fahrzeugschein" className="max-h-32 mx-auto rounded object-contain" />
                  <p className="text-xs text-muted-foreground">Gespeicherter Fahrzeugschein</p>
                  <div className="flex gap-2 justify-center pt-1">
                    <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                      <Camera className="w-3.5 h-3.5" />
                      Foto aufnehmen
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleScanRegistration}
                        disabled={scanning}
                      />
                    </label>
                    <span className="text-xs text-muted-foreground">|</span>
                    <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                      <Upload className="w-3.5 h-3.5" />
                      Aus Galerie wählen
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleScanRegistration}
                        disabled={scanning}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 py-2">
                  <Camera className="w-8 h-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Fahrzeugschein hinzufügen</p>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <label className="cursor-pointer flex-1 sm:flex-none">
                      <span className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 w-full" data-testid="scan-registration-camera-btn">
                        <Camera className="w-4 h-4" />
                        Foto aufnehmen
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleScanRegistration}
                        disabled={scanning}
                        data-testid="scan-registration-input"
                      />
                    </label>
                    <label className="cursor-pointer flex-1 sm:flex-none">
                      <span className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-input bg-background hover:bg-accent w-full" data-testid="scan-registration-gallery-btn">
                        <Upload className="w-4 h-4" />
                        Aus Galerie
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleScanRegistration}
                        disabled={scanning}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">Daten werden automatisch erkannt</p>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Customer */}
                {!editingVehicle && (
                  <div className="col-span-1 sm:col-span-2 space-y-2">
                    <Label>Kunde *</Label>
                    <Select
                      value={formData.customer_id}
                      onValueChange={(v) => setFormData({ ...formData, customer_id: v })}
                      required
                    >
                      <SelectTrigger data-testid="vehicle-customer-select">
                        <SelectValue placeholder="Kunde auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {sortedCustomers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Kennzeichen */}
                <div className="space-y-2">
                  <Label>Kennzeichen *</Label>
                  <div className="flex gap-1.5">
                    <Input
                      value={formData.license_plate}
                      onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
                      placeholder="B-AB 1234"
                      required
                      data-testid="vehicle-plate-input"
                    />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, license_plate: t.toUpperCase().replace(/\s+/g, " ") }))} />
                  </div>
                </div>

                {/* VIN */}
                <div className="space-y-2">
                  <Label>Fahrgestellnummer (VIN) *</Label>
                  <div className="flex gap-1.5">
                    <Input
                      value={formData.vin}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17);
                        setFormData({ ...formData, vin: val });
                      }}
                      placeholder="WVWZZZ1KZXW123456"
                      required
                      maxLength={17}
                      className={formData.vin && formData.vin.length !== 17 ? "border-orange-400 focus-visible:ring-orange-400" : ""}
                      data-testid="vehicle-vin-input"
                    />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, vin: t.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17) }))} />
                    <BarcodeScanner
                      title="VIN-Barcode scannen"
                      description="Halte den VIN-Barcode (z.B. an B-Säule oder Motorraum) vor die Kamera."
                      onScan={(code) => {
                        const cleaned = (code || "").toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17);
                        setFormData((f) => ({ ...f, vin: cleaned }));
                      }}
                    />
                    <VinPhotoScanner
                      onResult={(vin) => {
                        const cleaned = (vin || "").toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17);
                        setFormData((f) => ({ ...f, vin: cleaned }));
                      }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formData.vin ? `${formData.vin.length}/17 Zeichen` : "17 Zeichen erforderlich"}
                    {formData.vin && formData.vin.length === 17 && <span className="text-green-600 ml-2">Gültige Länge</span>}
                  </p>
                </div>
              </div>

              {/* Marke & Modell */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label className="mb-2 block">Marke *</Label>
                  <Popover open={brandOpen} onOpenChange={setBrandOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="vehicle-brand-select">
                        <span className="truncate">{formData.brand || "Marke wählen..."}</span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Marke suchen..." />
                        <CommandList>
                          <CommandEmpty>Keine Marke gefunden</CommandEmpty>
                          <CommandGroup>
                            {brandList.map((b) => (
                              <CommandItem
                                key={b}
                                value={b}
                                onSelect={() => {
                                  setFormData({ ...formData, brand: b, model: "" });
                                  setBrandOpen(false);
                                  setManualModelInput(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", formData.brand === b ? "opacity-100" : "opacity-0")} />
                                {b}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Model */}
                <div className="flex-1">
                  <Label className="mb-2 block">Modell</Label>
                  {!manualModelInput && modelList.length > 0 ? (
                    <Popover open={modelOpen} onOpenChange={setModelOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="vehicle-model-select">
                          <span className="truncate">{formData.model || "Modell wählen..."}</span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Modell suchen..." />
                          <CommandList>
                            <CommandEmpty>Nicht in der Liste – unten "Manuell" klicken</CommandEmpty>
                            <CommandGroup>
                              {modelList.map((m) => (
                                <CommandItem
                                  key={m}
                                  value={m}
                                  onSelect={() => {
                                    setFormData({ ...formData, model: m });
                                    setModelOpen(false);
                                  }}
                                >
                                  <Check className={cn("mr-2 h-4 w-4", formData.model === m ? "opacity-100" : "opacity-0")} />
                                  {m}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="z.B. Golf, 3er, C-Klasse..."
                      data-testid="vehicle-model-input"
                    />
                  )}
                </div>
              </div>
              {modelList.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-primary hover:underline -mt-2"
                  onClick={() => { setManualModelInput(!manualModelInput); setFormData({ ...formData, model: "" }); }}
                  data-testid="toggle-manual-model"
                >
                  {manualModelInput ? "Aus Liste wählen" : "Modell nicht gefunden? Manuell eingeben"}
                </button>
              )}

              {/* Restliche Felder */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Modellbeschreibung */}
                <div className="col-span-1 sm:col-span-2 space-y-2">
                  <Label>Modellbeschreibung (optional)</Label>
                  <div className="flex gap-1.5">
                    <Input
                      value={formData.model_description}
                      onChange={(e) => setFormData({ ...formData, model_description: e.target.value })}
                      placeholder="z.B. 320D, 2.0 TDI, 1.6 HDi, 150 PS..."
                      data-testid="vehicle-model-description-input"
                    />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, model_description: t }))} />
                  </div>
                </div>

                {/* Erstzulassung */}
                <div className="space-y-2">
                  <Label>Erstzulassung</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="vehicle-registration-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {registrationDate ? format(registrationDate, "dd.MM.yyyy", { locale: de }) : "Datum wählen"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={registrationDate}
                        onSelect={(d) => {
                          setRegistrationDate(d);
                          if (d) setFormData({ ...formData, year: d.getFullYear() });
                        }}
                        locale={de}
                        captionLayout="dropdown-buttons"
                        fromYear={1950}
                        toYear={new Date().getFullYear() + 1}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Kilometerstand */}
                <div className="space-y-2">
                  <Label>Kilometerstand</Label>
                  <div className="flex gap-1.5">
                    <Input
                      type="number"
                      min="0"
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                      placeholder="50000"
                      data-testid="vehicle-mileage-input"
                    />
                    <VoiceInputButton onResult={(t) => setFormData((f) => ({ ...f, mileage: t.replace(/\D/g, "") }))} />
                  </div>
                </div>

                {/* Letzte Inspektion */}
                <div className="col-span-1 sm:col-span-2 space-y-2">
                  <Label>Letzte Inspektion</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="vehicle-inspection-date">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {inspectionDate ? format(inspectionDate, "dd.MM.yyyy", { locale: de }) : "Datum wählen"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={inspectionDate} onSelect={setInspectionDate} locale={de} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                <Button type="submit" className="btn-primary" data-testid="vehicle-submit-button">
                  {editingVehicle ? "Speichern" : "Erstellen"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Suchen: Kennzeichen, VIN, Marke, Kunde..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="vehicle-search-input"
        />
      </div>

      {/* Table */}
      <Card className="border shadow-sm overflow-hidden" data-testid="vehicles-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="table-header">Fahrzeug</TableHead>
                  <TableHead className="table-header hidden sm:table-cell">Kunde</TableHead>
                  <TableHead className="table-header">Kennzeichen</TableHead>
                  <TableHead className="table-header hidden lg:table-cell">VIN</TableHead>
                  <TableHead className="table-header hidden md:table-cell">KM-Stand</TableHead>
                  <TableHead className="table-header hidden xl:table-cell">Erstzulassung</TableHead>
                  <TableHead className="table-header w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVehicles.length > 0 ? (
                  filteredVehicles.map((vehicle) => (
                    <TableRow key={vehicle.id} className="hover:bg-muted/30" data-testid={`vehicle-row-${vehicle.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Car className="w-4 h-4 text-green-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold truncate">{vehicle.brand} {vehicle.model}</p>
                            <p className="text-xs text-muted-foreground">
                              {vehicle.model_description && <span className="mr-2">{vehicle.model_description}</span>}
                              {vehicle.first_registration
                                ? new Date(vehicle.first_registration).toLocaleDateString("de-DE")
                                : `Baujahr ${vehicle.year}`}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{vehicle.customer_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono font-semibold text-primary text-sm">{vehicle.license_plate}</span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <span className="font-mono text-xs text-muted-foreground">{vehicle.vin || "-"}</span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {vehicle.mileage ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Gauge className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span>{vehicle.mileage.toLocaleString("de-DE")} km</span>
                          </div>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell">
                        {vehicle.first_registration ? (
                          <div className="flex items-center gap-1 text-sm">
                            <CalendarIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span>{new Date(vehicle.first_registration).toLocaleDateString("de-DE")}</span>
                          </div>
                        ) : vehicle.last_inspection ? (
                          <div className="flex items-center gap-1 text-sm">
                            <CalendarIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            <span>{new Date(vehicle.last_inspection).toLocaleDateString("de-DE")}</span>
                          </div>
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`vehicle-menu-${vehicle.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(vehicle)} data-testid={`edit-vehicle-${vehicle.id}`}>
                              <Pencil className="w-4 h-4 mr-2" /> Bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(vehicle.id)} className="text-destructive" data-testid={`delete-vehicle-${vehicle.id}`}>
                              <Trash2 className="w-4 h-4 mr-2" /> Löschen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {searchTerm ? "Keine Fahrzeuge gefunden" : "Noch keine Fahrzeuge vorhanden"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Smart Scan Dialog */}
      <SmartScanDialog
        open={smartScanOpen}
        onOpenChange={setSmartScanOpen}
        customers={customers}
        onComplete={handleSmartScanComplete}
      />
    </div>
  );
};

export default VehiclesPage;
